// Module 10.6 — the visual vocabulary: colour-blind-safe hues paired with a redundant non-colour cue
// (shape for assets, motion/hatch/dim/outage for service status). Colour is NEVER the only channel.
import type { Fuel, NodeClass } from "../../domain/index";
import type { Hex, GlyphShape, WallMotion, WallStatus, AssetStatus } from "./spec";

// Okabe-Ito colour-blind-safe fuel hues (Batch C 4.4) — matched to the legend + the voltage-tier set
// so the whole map shares one CVD-safe vocabulary. Every plant ALSO carries a redundant FUEL_ICON,
// so colour is never the only channel. Gas variants split across orange/vermillion/rose/grey to stay
// distinguishable; clean fuels on teal/yellow/sky/deep-blue.
export const FUEL_COLOR: Record<Fuel, Hex> = {
  NUCLEAR: "#332288", // blue-purple
  HYDRO: "#0072B2", // deep blue
  GAS_CCGT: "#E69F00", // orange
  GAS_CHP: "#D55E00", // vermillion
  GAS_COGEN: "#D55E00", // vermillion
  GAS_PEAKER: "#CC79A7", // rose
  GAS_OIL_STEAM: "#999999", // grey (rare/legacy)
  GAS: "#E69F00", // orange (generic)
  WIND: "#009E73", // teal
  SOLAR: "#F0E442", // yellow
  BATTERY: "#56B4E9", // sky-blue
  BIOGAS_LFG: "#CC79A7", // rose
  BIOENERGY: "#009E73", // teal
  WASTE_TO_ENERGY: "#999999", // grey
  BIOGAS: "#CC79A7", // rose
  EFW: "#999999", // grey
  OTHER: "#999999", // grey
};

// Redundant fuel icon — the plant is identifiable without relying on colour.
export const FUEL_ICON: Record<Fuel, string> = {
  NUCLEAR: "atom",
  HYDRO: "wave",
  GAS_CCGT: "flame",
  GAS_CHP: "flame",
  GAS_COGEN: "flame",
  GAS_PEAKER: "flame",
  GAS_OIL_STEAM: "flame",
  GAS: "flame",
  WIND: "turbine",
  SOLAR: "sun",
  BATTERY: "battery",
  BIOGAS_LFG: "leaf",
  BIOENERGY: "leaf",
  WASTE_TO_ENERGY: "recycle",
  BIOGAS: "leaf",
  EFW: "recycle",
  OTHER: "dot",
};

// Asset glyph shape by node class — distinguishable in monochrome.
export const NODE_SHAPE: Record<NodeClass, GlyphShape> = {
  generator: "triangle",
  substation: "square",
  switch: "diamond",
  transformer: "hexagon",
  junction: "circle",
  consumer: "pin",
  prosumer: "pin",
};

// Distinct base hues for the two parcel kinds (Okabe-Ito: deep-blue consumer vs teal prosumer).
export const KIND_BASE_HUE: Record<"consumer" | "prosumer", Hex> = {
  consumer: "#0072B2",
  prosumer: "#009E73",
};

export interface StatusStyle {
  motion: WallMotion;
  hatch: boolean;
  dim: boolean;
  outageGlyph: boolean;
  color: Hex;
}

// Service grammar — each status carries a distinct NON-colour signature (motion|hatch|outage|dim),
// so the four states read apart without any colour. "unknown" = v1-shaped snapshot with no runtime.
// Okabe-Ito status hues (Batch C 4.4); the motion/hatch/dim/outageGlyph fields are UNCHANGED — they
// remain the primary NON-colour channel so each state reads apart without colour.
export const STATUS_STYLE: Record<WallStatus, StatusStyle> = {
  normal: { motion: "steady", hatch: false, dim: false, outageGlyph: false, color: "#009E73" }, // teal
  stressed: { motion: "pulse", hatch: false, dim: false, outageGlyph: false, color: "#F0E442" }, // yellow + pulse
  shed: { motion: "steady", hatch: true, dim: true, outageGlyph: false, color: "#56B4E9" }, // sky-blue + hatch/dim
  dark: { motion: "none", hatch: false, dim: true, outageGlyph: true, color: "#555555" }, // grey + outage glyph
  unknown: { motion: "steady", hatch: false, dim: false, outageGlyph: false, color: "#999999" }, // neutral grey
};

// P14 per-node CONDITION colour (facility parcels recolour by service state). One CVD-safe Okabe-Ito
// ramp shared across all node types: `normal` keeps the asset's IDENTITY hue (plant orange / substation
// blue — passed in), an abnormal state OVERRIDES it with the danger hue (matching the edge loading ramp:
// warn yellow / red vermillion) and outage dims to dead grey. Mirrors the wall service grammar so the
// whole map speaks one condition vocabulary. `baseAlpha` is the parcel's own opacity (wall vs fill).
export function assetConditionStyle(status: AssetStatus, identityHex: Hex, baseAlpha: number): { hex: Hex; alpha: number } {
  switch (status) {
    case "elevated":
      return { hex: "#F0E442", alpha: baseAlpha }; // ≥55% — busy peak-hour band, Okabe-Ito warn yellow
    case "stressed":
      return { hex: "#E69F00", alpha: baseAlpha }; // ≥80% — heavily loaded, orange
    case "shortage":
      return { hex: "#D55E00", alpha: baseAlpha }; // ≥100% incident loading — vermillion (overload)
    case "outage":
      return { hex: "#555555", alpha: baseAlpha * 0.45 }; // de-energized → dead grey, dimmed
    default:
      return { hex: identityHex, alpha: baseAlpha }; // normal → the asset's own identity hue
  }
}

// Phase 1.3 — ISLAND MEMBERSHIP colour (cascade legibility): a fault that splits the grid into more
// than one live island should be VISIBLE as distinct islands, not just inferable from a topology
// change. Hash islandId → one of a small CVD-safe (Okabe-Ito) hue set; wraps for >8 simultaneous
// islands (an extreme case — GTA has never actually shown more than a handful live). A de-energized /
// no-island edge (islandId < 0, the packer's sentinel for "absent") gets `null` — no tint — so it
// falls back to its ordinary tier colour; island tinting is only meaningful once a fault has actually
// split the grid, never as a permanent recolour of the single-island steady state.
const ISLAND_HUES: readonly Hex[] = [
  "#0072B2", "#E69F00", "#009E73", "#CC79A7", "#D55E00", "#56B4E9", "#F0E442", "#332288",
];
export function islandColor(islandId: number): Hex | null {
  if (islandId < 0) return null;
  return ISLAND_HUES[islandId % ISLAND_HUES.length]!;
}

// Phase 1.1 — per-bus VOLTAGE MAGNITUDE band colour (the voltage overlay channel). The engine computes
// a full LinDistFlow profile (busVMagPu) every tick; this maps the pu value to a CVD-safe ramp that
// reuses the same Okabe-Ito hues as the loading band so the map speaks one vocabulary. A blacked-out
// bus (vMagPu ≤ 0) keeps the dead-grey so outage still reads under voltage mode. The 0.95 threshold
// mirrors the engine's ANSI C84.1 SAG_PU floor; 0.97 is the "marginal" early-warning band.
export function voltageBandStyle(vMagPu: number): { hex: Hex; alpha: number } {
  if (vMagPu <= 0) return { hex: "#37474f", alpha: 0.35 }; // de-energized → dead grey (outage visible)
  if (vMagPu < 0.93) return { hex: "#D55E00", alpha: 0.9 }; // deep sag — vermillion
  if (vMagPu < 0.95) return { hex: "#E69F00", alpha: 0.9 }; // below ANSI sag floor — orange
  if (vMagPu < 0.97) return { hex: "#F0E442", alpha: 0.9 }; // marginal — yellow
  return { hex: "#009E73", alpha: 0.9 }; // healthy — green
}
